import { Show } from "solid-js";
import { abilityDef, captainMovedThisTurn, isInCheck } from "../../core";
import type { GameController } from "../../app/controller";

export function ActionPanel(props: { controller: GameController; online?: boolean }) {
  const c = props.controller;
  const state = () => c.store.state;
  const me = () => state().players[c.viewer()];

  // The free Captain move is available when you have a Captain on the board and
  // haven't used it this turn.
  const captainReady = () =>
    c.canAct() &&
    !captainMovedThisTurn(state()) &&
    state().board.some((p) => p?.type === "captain" && p.owner === state().turn);

  // The side to move may not pass while in check — they must move a piece or
  // deploy one to block.
  const inCheck = () => isInCheck(state(), state().turn);

  const hint = () => {
    if (props.online && !c.canAct()) return "Waiting for your opponent's move…";
    if (c.mustDiscard()) {
      const over = me().hand.length - state().config.handLimit;
      return `Hand over the limit — tap ${over} card${over === 1 ? "" : "s"} to discard.`;
    }
    if (inCheck()) return "You're in check — move a piece, or deploy one to block.";
    const sel = c.selection();
    if (sel?.kind === "card") return "Pick a highlighted home square to deploy.";
    if (sel?.kind === "piece") return "Pick a highlighted square to move, or tap the piece again.";
    if (sel?.kind === "ability") {
      if (abilityDef(sel.ability).target === "recall") {
        return "Pick a highlighted piece to return to its owner's hand.";
      }
      return sel.from === null
        ? "Pick a highlighted piece for this card to move."
        : "Pick a highlighted square to send it to.";
    }
    if (sel?.kind === "revive") return "Choose a fallen piece to bring back to your hand.";
    return "Play a card or move a piece — that's your turn.";
  };

  return (
    <div class="actions">
      <div class="action-hint">{hint()}</div>

      <div class="draw-note">
        You draw a card automatically each turn ({me().deck.length} left in deck).
      </div>

      <Show when={captainReady()}>
        <div class="captain-note">Captain's free move available — it won't use your turn.</div>
      </Show>

      <Show when={c.store.error}>
        <div class="error" onClick={() => c.store.clearError()}>
          {c.store.error}
        </div>
      </Show>
    </div>
  );
}
